class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onOutsidePointer = this.handleOutsidePointer.bind(this);
    this.onScroll = this.handleScroll.bind(this);
    this.onResize = this.positionUnderCart.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.revealHeader();
    this.positionUnderCart();
    this.notification.classList.add('animate', 'active');

    // Not tied to transitionend: with reduced motion there is no transition,
    // and focus would never move into the dialog.
    window.clearTimeout(this.focusTimer);
    this.focusTimer = window.setTimeout(() => {
      if (!this.notification.classList.contains('active')) return;
      this.notification.focus({ preventScroll: true });
      trapFocus(this.notification);
    }, 60);

    this.openScrollY = window.scrollY;
    document.body.addEventListener('click', this.onBodyClick);
    document.addEventListener('pointerdown', this.onOutsidePointer, true);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);

    this.dispatchCartViewEvent();
  }

  // The popover points at the cart icon, so the header must be on screen.
  // Cut the hide/reveal transition short so the icon is measured where it
  // will actually sit, not mid-slide.
  revealHeader() {
    const headerSection = document.querySelector('.solvex-header-section');
    if (!headerSection || !headerSection.classList.contains('solvex-header-section--hidden')) return;
    const previousTransition = headerSection.style.transition;
    headerSection.style.transition = 'none';
    headerSection.classList.remove('solvex-header-section--hidden');
    headerSection.getBoundingClientRect();
    headerSection.style.transition = previousTransition;
  }

  positionUnderCart() {
    const viewportWidth = document.documentElement.clientWidth;
    const gutter = viewportWidth < 750 ? 12 : 24;
    const cartIcon = Array.from(document.querySelectorAll('#cart-icon-bubble')).find((element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });

    let top = 80;
    let caretRight = 24;
    if (cartIcon) {
      const rect = cartIcon.getBoundingClientRect();
      top = Math.max(8, rect.bottom + 12);
      caretRight = viewportWidth - (rect.left + rect.width / 2) - gutter - 7;
    }

    const popoverWidth = this.notification.offsetWidth || 340;
    caretRight = Math.min(Math.max(caretRight, 14), popoverWidth - 28);

    this.notification.style.setProperty('--solvex-cart-pop-top', `${Math.round(top)}px`);
    this.notification.style.setProperty('--solvex-cart-pop-right', `${gutter}px`);
    this.notification.style.setProperty('--solvex-cart-pop-caret', `${Math.round(caretRight)}px`);
  }

  // Any press outside the popover closes it straight away, before the press
  // lands, so the shopper's tap still does what they meant it to.
  handleOutsidePointer(evt) {
    if (this.notification.contains(evt.target)) return;
    this.activeElement = null;
    this.close();
  }

  handleScroll() {
    if (Math.abs(window.scrollY - this.openScrollY) > 24) {
      this.activeElement = null;
      this.close();
    }
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    if (!this.notification.classList.contains('active')) return;
    window.clearTimeout(this.focusTimer);
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    document.removeEventListener('pointerdown', this.onOutsidePointer, true);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-notification')) customElements.define('cart-notification', CartNotification);
